import { useState } from 'react';
import type { AdminAccount, AdminAccountDetail, AdminAnalyticsExclusion, RevealedIdentity, Tier } from '@boardflight/shared';
import { ADMIN_ACCOUNT_TIERS } from '@boardflight/shared';
import { api, download, post, put } from '../api.js';
import { Async, Filter, ListMore, Modal, Panel, Time, useAsync, useListLength, useSelection } from '../ui.js';
import { AccountActivity, AccountBoards } from './AccountActivity.js';
import { AccountAssistant } from './Assistant.js';
import { FeatureList, TierPill } from './FeatureList.js';

const ANALYTICS_FILTER = [
  { value: '', label: 'Any' },
  { value: '1', label: 'Excluded' },
] as const;

/** Exactly that tier. `free` is nearly everybody, which is the list with no filter. */
const TIER_FILTER = [
  { value: '', label: 'Any' },
  ...ADMIN_ACCOUNT_TIERS.map((tier) => ({ value: tier, label: tier })),
];

/** The tier a link asked for (`#accounts?tier=alpha`, from the Features screen), or none. */
function tierFromHash(): string {
  const asked = /[?&]tier=([a-z]+)/.exec(window.location.hash)?.[1] ?? '';
  return (ADMIN_ACCOUNT_TIERS as readonly string[]).includes(asked) ? asked : '';
}

/** Newest signups is the landing list; "last active" is who has been using BoardFlight lately. */
const ORDER_FILTER = [
  { value: '', label: 'Newest' },
  { value: 'active', label: 'Last active' },
] as const;

/**
 * The account view's sections, as tabs. Activity first: it is what an account is most often opened
 * to read, and the tier — the other common errand — is in the header on every tab.
 */
const ACCOUNT_TABS = [
  { key: 'activity', label: 'Activity' },
  { key: 'boards', label: 'Boards' },
  // Teams and what the account has in them, beside what its tier gives it anywhere: one question.
  { key: 'teams', label: 'Teams & features' },
  // The account's questions to the assistant and what they cost, by day (docs/assistant-plan.md §11.3).
  { key: 'assistant', label: 'Assistant' },
] as const;
type AccountTab = (typeof ACCOUNT_TABS)[number]['key'];

/** The tab named in the fragment, or the first one — a stale or hand-typed name is no opinion. */
function tabOf(raw: string | undefined): AccountTab {
  // `features` was a tab of its own until 2026-10-28; a link to it still lands on the same table.
  if (raw === 'features') return 'teams';
  return ACCOUNT_TABS.find((tab) => tab.key === raw)?.key ?? 'activity';
}

/**
 * Accounts: find one, see what it resolves to, change its tier (§2), keep it out of analytics
 * (§3.3), and read what it has done (§3.4). The plan is a team's, and is changed on Teams.
 */
export function Accounts() {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  // Seeded from the fragment, so the Analytics tab's "leaves out N accounts" can link straight here.
  const [excluded, setExcluded] = useState(() =>
    window.location.hash.includes('?excluded=1') ? '1' : '',
  );
  // `#accounts/<id>/<tab>`: the account and which of its tabs, so either can be pasted (§5.2).
  const [selected, setSelected] = useSelection('accounts');
  const [selectedId, selectedTab] = selected ? selected.split('/') : [];
  const [order, setOrder] = useState('');
  // Seeded from the fragment too, so the Features screen's counts can link to who they count.
  const [tier, setTier] = useState(tierFromHash);
  const [shown, showMore] = useListLength([search, excluded, tier, order].join('\n'));

  const list = useAsync(
    () =>
      api<{ items: AdminAccount[]; limit: number; total: number }>(
        `/v1/admin/accounts?q=${encodeURIComponent(search)}${excluded ? '&excluded=1' : ''}` +
          `${tier ? `&tier=${tier}` : ''}${order ? `&order=${order}` : ''}&limit=${shown}`,
      ),
    [search, excluded, tier, order, shown],
  );

  return (
    <>
      <Panel
        title="Accounts"
        actions={
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(query.trim());
            }}
          >
            <Filter label="Order" value={order} onChange={setOrder} options={ORDER_FILTER} />
            <Filter label="Tier" value={tier} onChange={setTier} options={TIER_FILTER} />
            <Filter label="Analytics" value={excluded} onChange={setExcluded} options={ANALYTICS_FILTER} />
            <label className="filter">
              <span className="muted small">Search</span>
              <input
              type="text"
              value={query}
              placeholder="Email, handle or id"
              onChange={(e) => setQuery(e.target.value)}
            />
            </label>
            <button type="submit">Search</button>
            <NewGameEmails />
          </form>
        }
      >
        <Async state={list} empty="No accounts.">
          {(data) =>
            data.items.length === 0 ? (
              <p className="muted">
                {excluded && !tier
                  ? 'No account is excluded from analytics.'
                  : tier && !excluded
                    ? `No account is in the ${tier}.`
                    : excluded || tier
                      ? 'No account matches this filter.'
                      : 'No accounts.'}
              </p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Handle</th>
                      <th title="A creator is in a team; one who shipped has uploaded a build">Kind</th>
                      <th>Tier</th>
                      <th>Joined</th>
                      {/* Not "last played": no play names an account (§3.5). The newest sign-in or
                          refresh of any of its sessions — the website, the desktop app, the CLI. */}
                      <th title="When anything signed in as this account was last used: the website, the desktop app, the CLI or a plugin. Plays are not tied to accounts.">
                        Last active
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.email}
                          {row.admin ? <span className="pill" style={{ marginLeft: 6 }}>admin</span> : null}
                          {row.disabled ? <span className="pill off" style={{ marginLeft: 6 }}>disabled</span> : null}
                          {row.analyticsExcluded ? (
                            <span className="pill" style={{ marginLeft: 6 }} title="Excluded from analytics">
                              no analytics
                            </span>
                          ) : null}
                        </td>
                        <td className="mono">@{row.username}</td>
                        <td>
                          {row.shipped ? (
                            <span className="pill on" title={`In ${row.teams} team${row.teams === 1 ? '' : 's'}, has uploaded a build`}>creator · shipped</span>
                          ) : row.teams > 0 ? (
                            <span className="pill paid" title={`In ${row.teams} team${row.teams === 1 ? '' : 's'}`}>creator</span>
                          ) : (
                            <span className="muted small">player</span>
                          )}
                        </td>
                        <td>
                          <TierPill tier={row.tier} />
                        </td>
                        <td className="muted">
                          <Time at={row.createdAt} />
                        </td>
                        <td className="muted">
                          <Time at={row.lastActiveAt} />
                        </td>
                        <td>
                          <button onClick={() => setSelected(row.id)}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
        <ListMore state={list} shown={shown} noun="accounts" onMore={showMore} />
      </Panel>

      {selectedId ? (
        <Account
          userId={selectedId}
          tab={tabOf(selectedTab)}
          onTab={(tab) => setSelected(`${selectedId}/${tab}`)}
          onClose={() => setSelected(null)}
          onChanged={list.reload}
        />
      ) : null}
    </>
  );
}

/**
 * The accounts that asked for email about new games (`docs/new-game-emails.md`), as a CSV for
 * whatever the mail is sent from. Nothing here sends anything.
 *
 * A button in the list's head with the count on it, where it was a panel under every list. The
 * download is an action, not a view — every one writes `admin.new_game_emails_export` with its size,
 * the way the identity reveal does (§D7) — and the tooltip says so.
 */
function NewGameEmails() {
  const total = useAsync(() => api<{ count: number }>('/v1/admin/new-game-emails'), []);
  const [busy, setBusy] = useState(false);

  async function exportList() {
    setBusy(true);
    try {
      await download('/v1/admin/new-game-emails/export', { method: 'POST' });
      total.reload();
    } catch (failure) {
      window.alert(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void exportList()}
      disabled={busy || !total.data?.count}
      title="Accounts that asked for email about new games, disabled ones left out. Every download is recorded in the audit log."
    >
      {busy ? 'Preparing…' : `New-game emails${total.data ? ` (${total.data.count})` : ''} ↓`}
    </button>
  );
}

/**
 * One account, as a page of its own over the list (§5.2): the tier and the identity reveal in the
 * header, who it is in the line under it, and its sections as tabs. Activity and Boards read their
 * own route and do not wait for the account's; the other three are the account read itself.
 */
function Account({ userId, tab, onTab, onClose, onChanged }: {
  userId: string;
  tab: AccountTab;
  onTab: (tab: AccountTab) => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useAsync(() => api<AdminAccountDetail>(`/v1/admin/accounts/${userId}`), [userId]);
  const [revealed, setRevealed] = useState<RevealedIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Into the alpha or out of it confirms; the beta does not. The beta is something the person does
   * for themselves from their account, so an operator moving it is doing an errand, but the alpha
   * is only ever this console's, and it changes what the person has in every team they are in at
   * once — a playground switched to Claude Code starts its next sandbox without it, say.
   */
  async function changeTier(next: Tier, current: Tier, username: string) {
    if (next === current) return;
    if (next === 'alpha' || current === 'alpha') {
      const question =
        next === 'alpha'
          ? `Put @${username} in the alpha? Every alpha feature turns on for this account, in every team it is in.`
          : `Take @${username} out of the alpha, to ${next}? Every alpha feature turns off for this account, in every team it is in.`;
      if (!window.confirm(question)) {
        // The select is controlled, so re-reading puts it back on the tier the account has.
        detail.reload();
        return;
      }
    }
    setError(null);
    try {
      await put(`/v1/admin/accounts/${userId}/tier`, { tier: next });
      detail.reload();
      onChanged();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }

  async function reveal() {
    // Required, and refused server-side if blank: this is the one read in the system whose
    // justification exists nowhere but in the head of the person doing it (§D7).
    const note = window.prompt('Why are you revealing this identity? (recorded in the audit log)');
    if (note === null || note.trim() === '') return;
    setRevealed(await post<RevealedIdentity>(`/v1/admin/accounts/${userId}/reveal`, { note }));
  }

  const account = detail.data?.account;

  return (
    // Titled before the read lands, so the modal does not open blank and then re-label itself.
    <Modal
      full
      title={account?.email ?? 'Account'}
      onClose={onClose}
      actions={
        account ? (
          <>
            <label className="row" title="How early this account gets new features. The plan is the team's.">
              <span className="muted small">Tier</span>
              <select
                value={account.tier}
                onChange={(e) => void changeTier(e.target.value as Tier, account.tier, account.username)}
              >
                <option value="free">free</option>
                <option value="beta">beta</option>
                <option value="alpha">alpha</option>
              </select>
            </label>
            <AnalyticsExclusion
              userId={userId}
              exclusion={detail.data?.analyticsExclusion ?? null}
              onChanged={() => {
                detail.reload();
                onChanged();
              }}
            />
            <button onClick={() => void reveal()}>Reveal identity</button>
          </>
        ) : null
      }
      subhead={
        <>
          {account ? (
            <div className="row">
              <span className="mono">@{account.username}</span>
              <span className="muted">·</span>
              <span className="muted">
                joined <Time at={account.createdAt} />
              </span>
              <span className="muted">·</span>
              <span className="muted">
                {account.lastActiveAt === null ? (
                  'never signed in'
                ) : (
                  <>
                    last active <Time at={account.lastActiveAt} />
                  </>
                )}
              </span>
              {account.admin ? <span className="pill">admin</span> : null}
              {account.disabled ? <span className="pill off">disabled</span> : null}
              {detail.data?.analyticsExclusion ? (
                <span className="pill" title={`Excluded from analytics by ${detail.data.analyticsExclusion.createdBy ? `@${detail.data.analyticsExclusion.createdBy}` : 'a former admin'}`}>
                  no analytics · {detail.data.analyticsExclusion.note}
                </span>
              ) : null}
              {revealed ? (
                <>
                  <span className="muted">·</span>
                  <span>{revealed.displayName ?? 'no real name given'}</span>
                </>
              ) : null}
              {error ? <span className="error">{error}</span> : null}
            </div>
          ) : null}
          <nav className="tabs" role="tablist">
            {ACCOUNT_TABS.map((entry) => (
              <button
                key={entry.key}
                role="tab"
                aria-selected={entry.key === tab}
                className={entry.key === tab ? 'on' : undefined}
                onClick={() => onTab(entry.key)}
              >
                {entry.key === 'teams' && detail.data
                  ? `${entry.label} (${detail.data.teams.length})`
                  : entry.label}
              </button>
            ))}
          </nav>
        </>
      }
    >
      {tab === 'activity' ? <AccountActivity userId={userId} /> : null}
      {tab === 'boards' ? <AccountBoards userId={userId} /> : null}
      {tab === 'assistant' ? <AccountAssistant userId={userId} /> : null}
      {tab === 'teams' ? (
        <Async state={detail}>
          {(data) => (
            <>
              <Panel title="Teams">
                {data.teams.length === 0 ? (
                  <p className="muted">In no teams.</p>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Team</th>
                          <th>Role</th>
                          <th>Plan</th>
                          <th className="num">Seats</th>
                          <th>Features here</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.teams.map((team) => (
                          <tr key={team.id}>
                            <td>
                              <a href={`#teams/${team.id}`}>{team.name}</a>
                            </td>
                            <td className="muted">{team.role}</td>
                            <td>
                              <span className={team.plan === 'paid' ? 'pill paid' : 'pill'}>{team.plan}</span>
                            </td>
                            <td className="num">{team.playgroundSeats}</td>
                            {/* The pair, resolved — what this person actually has *in this team*,
                                which the Features tab, knowing only the tier, cannot say. */}
                            <td className="mono muted">{team.features.join(' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
              <Panel title="Features from its tier">
                {/* From the tier alone. A paid feature is the team's, and reads "per team" here
                    rather than a guess; the table above lists what this account has in each. */}
                <FeatureList features={data.features} other="per team" />
              </Panel>
            </>
          )}
        </Async>
      ) : null}
    </Modal>
  );
}

/**
 * Whether this account counts (§3.3). One row in `analytics_exclusions`, read by every analytics
 * query in both Workers — so this switch is the whole of it, here and on every team's dashboard.
 */
function AnalyticsExclusion({ userId, exclusion, onChanged }: {
  userId: string;
  exclusion: AdminAnalyticsExclusion | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function change(body: { excluded: true; note: string } | { excluded: false }) {
    setBusy(true);
    setError(null);
    try {
      await put(`/v1/admin/accounts/${userId}/analytics-exclusion`, body);
      onChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      // In a modal's head there is no line to write it on, so it is said once, now.
      window.alert(message);
    } finally {
      setBusy(false);
    }
  }

  function exclude() {
    // Required, and refused server-side if blank: the numbers get smaller, and the reason has to be
    // written down somewhere other than the head of whoever made them smaller.
    const note = window.prompt(
      'Why should this account not count? (e.g. "internal", "library reviewer" — recorded with the exclusion)',
      exclusion?.note ?? '',
    );
    if (note === null || note.trim() === '') return;
    void change({ excluded: true, note: note.trim() });
  }

  return exclusion ? (
    <>
      <button disabled={busy} onClick={exclude} title="Edit the reason it is not counted">
        Edit exclusion
      </button>
      <button disabled={busy} onClick={() => void change({ excluded: false })} title={error ?? 'Count this account in analytics again'}>
        Count again
      </button>
    </>
  ) : (
    <button
      disabled={busy}
      onClick={exclude}
      title={error ?? "Leave this account out of every analytics number here and on every team's dashboard, its history included"}
    >
      Exclude from analytics
    </button>
  );
}
